(ns app.domain.form.i18n
  "This namespace (in conjunction with the form editor) provides mechanisms
  for overriding (with localization) the texts used in form elements.

  Usage:

  Assume a form element `[:hello-world {}]` with corresponding render function(s)
  (eg. `app.domain.form.render/render` or `app.front.form.render/render`),
  which render a piece of text with `(tr [:hello-world.label] [username])`
  using the translation function bound to the current locale and with
  a translation dictionary entry of `\"Hello, %1\"` for the locale.

  To enable overriding the default translation entry, we:
  - Add the translation key `:hello-world.label` as an optional attribute of the form element
    and which value should be a translation map from keywords (locales) to strings (translation text).
  - Add a user-friendly placeholder for the attribute in `localized-attrs-config`:

  ```clojure
  (def localized-attrs-config {:hello-world.label {:placeholders     [\"%username%\"]
                                                   :fetch-from-dict? true}})
  ```

  - Add the form element and its attributes to the form editor config
  - Use the `tr-schema` function in the render function(s) to construct an alternative
    tr function."
  (:require [clojure.string :as str]
            [schema-tools.core :as st]
            [schema.core :as s]))

(s/defschema Placeholder
  "A user-friendly placeholder"
  (s/constrained
    s/Str
    (fn [s] (and (str/starts-with? s "%")
                 (str/ends-with? s "%")))))

(s/defschema Config
  {s/Keyword (st/optional-keys
               {:placeholders     [Placeholder]
                ;; Should we fetch the default from from translations?
                :fetch-from-dict? s/Bool
                :long-text?       s/Bool})})

(def localized-attrs-config
  "Map of attribute / translation key to corresponding options.

  Argument order and count must match the arguments of the translation entry."
  (s/validate
    Config
    {:sign-off.rezoning/applicant-signature            {:fetch-from-dict? true}
     :sign-off.rezoning/commissioner-signature         {:fetch-from-dict? true}
     :sign-off.rezoning/date                           {:fetch-from-dict? true}
     :sign-off.rezoning/declaration-section-content    {:fetch-from-dict? true
                                                        :long-text?       true
                                                        :placeholders     ["%applicant-full-name%"]}
     :sign-off.rezoning/declaration-section-label      {:fetch-from-dict? true}
     :sign-off.rezoning/declaration-section-title      {:fetch-from-dict? true}
     :sign-off.rezoning/municipality                   {:fetch-from-dict? true}
     :sign-off.rezoning/place-stamp-below              {:fetch-from-dict? true}
     :sign-off.rezoning/pre-consultation-completed     {:fetch-from-dict? true
                                                        :placeholders     ["%completion-date%"
                                                                           "%application-number%"]}
     :sign-off.rezoning/pre-consultation-info          {:fetch-from-dict? true
                                                        :long-text?       true}
     :sign-off.rezoning/pre-consultation-not-completed {:fetch-from-dict? true
                                                        :placeholders     ["%application-number%"]}}))

(def localized-attrs (-> localized-attrs-config keys set))

(defn- find-by-flag
  [flag]
  (->> localized-attrs-config
       (filter (comp flag val))
       keys
       set))

(def localized-attrs-with-defaults
  "The subset of attributes which should use the corresponding dictionary entry as the default value"
  (find-by-flag :fetch-from-dict?))

(def localized-attrs-with-long-text
  (find-by-flag :long-text?))

(defn localized?
  [k]
  (contains? localized-attrs k))

(defn localized-placeholders
  [k]
  (-> localized-attrs-config k :placeholders))

(defn tr-schema
  "Return a tr-like fn for translating resources when rendering a schema.

  If the schema defines any overrides, use them. Otherwise fallback to using
  the given tr function."
  [lang tr-fallback schema-opts]
  (fn do-tr
    ([resource-ids]
     (do-tr resource-ids nil))
    ([[k :as resource-ids] resource-args]
     (if-let [text-from-schema (get-in schema-opts [k lang])]
       (->> (zipmap (localized-placeholders k) resource-args)
            (reduce
              (fn [text [k v]]
                (str/replace text k (str v)))
              text-from-schema))
       (tr-fallback resource-ids resource-args)))))
